import type { RoutePlan } from '../api/types';

type Stop = RoutePlan['stops'][number];

/*
 * The places a round goes past rather than stops at.
 *
 * A pass-by round can hold two hundred of them: a wall of markers over the
 * line they describe, and a schedule nobody scrolls to the end of. Both the
 * map and the itinerary put them down together, so what counts as one — and
 * how many it takes to be in the way — lives here rather than in either.
 */

/**
 * Whether the round goes past this one rather than stopping at it.
 *
 * A start and an end have no stay either, but they are where the day begins
 * and finishes: the question only means anything in between.
 */
export function isPassedStop(stop: Stop): boolean {
  return stop.kind !== 'start' && stop.kind !== 'end' && stop.dwellMinutes === 0;
}

export function passedStopCount(plan: RoutePlan | null | undefined): number {
  return (plan?.stops ?? []).filter(isPassedStop).length;
}

/**
 * How many of them it takes to be worth putting down.
 *
 * Below this they are the interesting part of a round — a few things seen
 * along the way, each worth its own mark. Above it they stop describing the
 * route and start hiding it.
 */
export const PASSED_CLUTTER = 20;

export function passedStopsCrowd(plan: RoutePlan | null | undefined): boolean {
  return passedStopCount(plan) >= PASSED_CLUTTER;
}

/**
 * What number each stop wears, keyed by its place in the plan.
 *
 * Ordinarily that is its place in the plan: stop 7 is the seventh thing the
 * round does, and the list and the map count the same way.
 *
 * With the passed places put down it cannot be. A round of a hundred and
 * thirty-five that stops once showed that one stop as 104 — its true
 * position, counted through a hundred and thirty-four markers nobody could
 * see. So while they are hidden the ones left count themselves: the only
 * stop on that round is stop 1, which is what somebody reading the map
 * would say it was.
 *
 * The keys stay the true indices throughout, because that is what a click,
 * a focus and a reorder all mean.
 */
export function stopNumbering(
  plan: RoutePlan | null | undefined,
  fold: boolean,
): Map<number, number> {
  const numbers = new Map<number, number>();
  let shown = 0;

  (plan?.stops ?? []).forEach((stop, index) => {
    if (stop.kind === 'start' || stop.kind === 'end') return;
    if (fold && isPassedStop(stop)) return;
    shown += 1;
    numbers.set(index, fold ? shown : index);
  });

  return numbers;
}
